import { z } from 'zod';
import type { Device } from './events.ts';
import type { AnalysisWaiting } from './protection.ts';
import type { AgentAction, Feeling, IssueSeverity, Mission, ReportExit, ReportStrength, TestStatus, VisitExit, VisitStatus, VisitStrength } from './synthetic.ts';

/**
 * The first tests started from the home page before 4 November 2026 ("free analyses") had a public report
 * through a non-guessable slug, and an instant review of the site: their pages stay readable. New tests are
 * all made the same way (see site-analysis.ts). The missions and the URL rules here are used by every test.
 */

/** Visitors of the first tests from the home page (their public pages say "3 AI visitors"). */
export const ANALYSIS_VISITS = 3;

export const ANALYSIS_MISSION_PRESETS = ['signup', 'pricing', 'buy'] as const;
export type AnalysisMissionPreset = (typeof ANALYSIS_MISSION_PRESETS)[number];

/** Missions proposed on the public page; the report is in English so the texts are too. */
export const ANALYSIS_MISSIONS: Record<AnalysisMissionPreset, { label: string } & Mission> = {
  signup: {
    label: 'Sign up',
    task: 'Create an account or sign up for the product.',
    successCondition: 'The sign-up form was submitted or the site confirms that the account was created.',
  },
  pricing: {
    label: 'Find the price',
    task: 'Find out how much the product or service costs.',
    successCondition: 'You found a clear price for what this site offers.',
  },
  buy: {
    label: 'Buy',
    task: 'Choose a product or a plan and get as far as possible towards buying it.',
    successCondition: 'You reached the checkout or the payment step for something you chose.',
  },
};

/** Mission written by the visitor: the success condition is generic. */
export function customMission(task: string): Mission {
  return { task, successCondition: 'You did what the task asks, as far as the site allows without paying.' };
}

/** Accepts `example.com` as well as full URLs; only http(s). */
export const analysisUrlSchema = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() !== '' && !/^[a-z][a-z0-9+.-]*:\/\//i.test(v.trim()) ? `https://${v.trim()}` : v),
  z
    .string()
    .trim()
    .max(2048)
    .refine(
      (v) => {
        try {
          const u = new URL(v);
          return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.length > 0;
        } catch {
          return false;
        }
      },
      { message: 'Enter the address of your site, like example.com' },
    ),
);

/** Removes the fragment and normalizes the host, so the same site finds the same project. */
export function normalizeAnalysisUrl(raw: string): string {
  const u = new URL(raw);
  u.hash = '';
  u.hostname = u.hostname.toLowerCase();
  if ((u.protocol === 'https:' && u.port === '443') || (u.protocol === 'http:' && u.port === '80')) u.port = '';
  return u.toString();
}

// --- Instant site review (F3) ---

export const SITE_REVIEW_AREAS = ['clarity', 'navigation', 'cta', 'trust', 'copy'] as const;
export type SiteReviewArea = (typeof SITE_REVIEW_AREAS)[number];

export const siteReviewAreaLabel: Record<SiteReviewArea, string> = {
  clarity: 'Value proposition',
  navigation: 'Navigation',
  cta: 'Calls to action',
  trust: 'Trust',
  copy: 'Copy',
};

export interface SiteReviewDto {
  /** 2-4 sentences: what a first-time visitor understands and the biggest problem. */
  summary: string;
  areas: {
    area: SiteReviewArea;
    /** 1 = poor, 5 = excellent. */
    score: number;
    verdict: string;
    findings: string[];
  }[];
  /** Pages read for the review. */
  pages: { url: string; title: string }[];
  generatedAt: string;
}

export type SiteReviewStatus = 'pending' | 'done' | 'failed';

// --- Public DTOs (no internal ids, no costs) ---

export interface PublicIssueDto {
  visitIndex: number;
  stepIndex: number;
  personaName: string;
  severity: IssueSeverity;
  title: string;
  description: string;
  url: string;
}

export interface PublicVisitSummaryDto {
  index: number;
  personaName: string;
  personaSummary: string;
  device: Device;
  status: VisitStatus;
  outcome: string | null;
  exit: VisitExit | null;
  strengths: VisitStrength[];
  stepCount: number;
  durationMs: number | null;
  finalMood: number | null;
  /** Latest thought of the agent, shown live. */
  lastThought: string | null;
  intent: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface PublicAnalysisDto {
  slug: string;
  siteUrl: string;
  host: string;
  mission: Mission | null;
  status: TestStatus;
  createdAt: string;
  finishedAt: string | null;
  etaMs: number | null;
  /** Set while no visit has started: our runners are busy, or today's budget of the Free plan is spent. */
  waiting: AnalysisWaiting | null;
  visitsTotal: number;
  visitsFinished: number;
  siteReview: { status: SiteReviewStatus; review: SiteReviewDto | null };
  visits: PublicVisitSummaryDto[];
  issues: PublicIssueDto[];
  report: {
    /** The verdict in a few words (missing on older reports). */
    headline?: string;
    summary: string;
    highlights: string[];
    groups: { title: string; severity: IssueSeverity; description: string; fix?: string; issues: PublicIssueDto[] }[];
    fixPrompt: string;
    /** What worked, each with the moments that show it (visit index and step). */
    strengths: (Omit<ReportStrength, 'moments'> & { moments: { visitIndex: number; stepIndex: number }[] })[];
    /** Why people left, by reason and page. */
    exits: (Omit<ReportExit, 'visitIds'> & { visitIndexes: number[] })[];
  } | null;
  /** The test this one re-runs (after fixes), and the latest re-run of this one. */
  previous: { slug: string; issues: number; createdAt: string } | null;
  rerun: { slug: string; createdAt: string } | null;
  /** Finished: the author can run it again (same personas, mission and seed), for the same visits. */
  canRerun: boolean;
}



export interface PublicStepDto {
  index: number;
  ts: string;
  url: string;
  thought: string;
  mood: number;
  feeling: Feeling | null;
  action: AgentAction;
  result: string | null;
}

export interface PublicVisitDetailDto extends PublicVisitSummaryDto {
  slug: string;
  host: string;
  sourceLabel: string;
  entryPath: string;
  steps: PublicStepDto[];
  issues: PublicIssueDto[];
  visitsTotal: number;
}
